// components/admin/UserPermissionsTab.js
// Tab הרשאות בתוך dialog עריכת משתמש
//
// API:
//   GET /users/{id}/permissions → { units: [{unit_id, can_edit}], categories: [{category_id, can_edit}] }
//   POST /users/{id}/permissions → save
//   GET /units → רשימת כל היחידות (לcheckboxes)
//   GET /categories/list-active → רשימת כל הקטגוריות הפעילות (flat, for checkboxes)
//
// Flow: load permissions + units + categories on mount → show checkboxes → save

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Paper,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { useApiMutation } from '../../hooks/useApi';

// =============================================================================
// UserPermissionsTab
// =============================================================================
export default function UserPermissionsTab({ userId, onSaved }) {
  const { t } = useTranslation();
  const { mutate, loading: saving } = useApiMutation();

  // Data
  const [allUnits, setAllUnits] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [unitPerms, setUnitPerms] = useState([]); // [{unit_id, can_edit}]
  const [categoryPerms, setCategoryPerms] = useState([]); // [{category_id, can_edit}]

  // Status
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  // ===================================================================
  // Load data on mount / userId change
  // ===================================================================
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);
    setError('');
    setSuccess('');
    setDirty(false);

    const load = async () => {
      try {
        // Parallel fetch: permissions + units + categories
        const [permsRes, unitsRes, categoriesRes] = await Promise.all([
          api.get(`/users/${userId}/permissions`),
          api.get('/units'),
          api.get('/categories/list-active'),
        ]);

        if (cancelled) return;

        const permsData = permsRes.data?.data || permsRes.data || {};
        const unitsData = unitsRes.data?.data || unitsRes.data || [];
        const catsData = categoriesRes.data?.data || categoriesRes.data || [];

        setAllUnits(Array.isArray(unitsData) ? unitsData : []);
        setAllCategories(Array.isArray(catsData) ? catsData : []);
        setUnitPerms(Array.isArray(permsData.units) ? permsData.units : []);
        setCategoryPerms(Array.isArray(permsData.categories) ? permsData.categories : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.data?.message || err.message || t('common.error'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, t]);

  // ===================================================================
  // Permission toggle handlers
  // ===================================================================
  const isUnitChecked = useCallback((unitId) => {
    return unitPerms.some((p) => p.unit_id === unitId);
  }, [unitPerms]);

  const getUnitCanEdit = useCallback((unitId) => {
    const perm = unitPerms.find((p) => p.unit_id === unitId);
    return perm ? perm.can_edit : true; // default: can_edit=true when first added
  }, [unitPerms]);

  const toggleUnit = useCallback((unitId) => {
    setDirty(true);
    setUnitPerms((prev) => {
      const exists = prev.find((p) => p.unit_id === unitId);
      if (exists) {
        // Remove
        return prev.filter((p) => p.unit_id !== unitId);
      } else {
        // Add with can_edit=true
        return [...prev, { unit_id: unitId, can_edit: true }];
      }
    });
  }, []);

  const setUnitCanEdit = useCallback((unitId, canEdit) => {
    setDirty(true);
    setUnitPerms((prev) =>
      prev.map((p) => p.unit_id === unitId ? { ...p, can_edit: canEdit } : p)
    );
  }, []);

  const isCategoryChecked = useCallback((categoryId) => {
    return categoryPerms.some((p) => p.category_id === categoryId);
  }, [categoryPerms]);

  const getCategoryCanEdit = useCallback((categoryId) => {
    const perm = categoryPerms.find((p) => p.category_id === categoryId);
    return perm ? perm.can_edit : true;
  }, [categoryPerms]);

  const toggleCategory = useCallback((categoryId) => {
    setDirty(true);
    setCategoryPerms((prev) => {
      const exists = prev.find((p) => p.category_id === categoryId);
      if (exists) {
        return prev.filter((p) => p.category_id !== categoryId);
      } else {
        return [...prev, { category_id: categoryId, can_edit: true }];
      }
    });
  }, []);

  const setCategoryCanEdit = useCallback((categoryId, canEdit) => {
    setDirty(true);
    setCategoryPerms((prev) =>
      prev.map((p) => p.category_id === categoryId ? { ...p, can_edit: canEdit } : p)
    );
  }, []);

  // ===================================================================
  // Save permissions
  // ===================================================================
  const handleSave = useCallback(async () => {
    if (saving) return;
    setError('');
    setSuccess('');

    const body = {
      units: unitPerms.map(({ unit_id, can_edit }) => ({ unit_id, can_edit })),
      categories: categoryPerms.map(({ category_id, can_edit }) => ({ category_id, can_edit })),
    };

    const result = await mutate('post', `/users/${userId}/permissions`, body);

    if (result.success) {
      setDirty(false);
      setSuccess(t('common.success'));
      if (onSaved) onSaved();
    } else {
      setError(result.error || t('common.error'));
    }
  }, [saving, unitPerms, categoryPerms, userId, mutate, onSaved, t]);

  // ===================================================================
  // Reset permissions
  // ===================================================================
  const handleReset = useCallback(async () => {
    if (saving) return;
    setError('');
    setSuccess('');

    // Send empty arrays = clear all permissions
    const result = await mutate('post', `/users/${userId}/permissions`, {
      units: [],
      categories: [],
    });

    if (result.success) {
      setUnitPerms([]);
      setCategoryPerms([]);
      setDirty(false);
      setSuccess(t('common.success'));
      if (onSaved) onSaved();
    } else {
      setError(result.error || t('common.error'));
    }
  }, [saving, userId, mutate, onSaved, t]);

  // Active units/categories only
  const activeUnits = useMemo(() =>
    allUnits.filter((u) => u.active !== false),
  [allUnits]);

  const activeCategories = useMemo(() =>
    allCategories.filter((c) => c.active !== false),
  [allCategories]);

  // ===================================================================
  // Render — Loading
  // ===================================================================
  if (loading) {
    return (
      <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  // ===================================================================
  // Permission row component
  // ===================================================================
  const PermissionRow = ({ checked, label, canEdit, onToggle, onCanEditChange, testIdPrefix }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
        px: 1,
        borderRadius: 1,
        bgcolor: checked ? 'action.hover' : 'transparent',
        transition: 'background-color 0.15s',
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={onToggle}
            size="small"
            data-testid={`${testIdPrefix}__checkbox`}
          />
        }
        label={<Typography variant="body2">{label}</Typography>}
        sx={{ flex: 1 }}
      />
      {checked && (
        <ToggleButtonGroup
          value={canEdit ? 'edit' : 'view'}
          exclusive
          onChange={(_, val) => val && onCanEditChange(val === 'edit')}
          size="small"
          data-testid={`${testIdPrefix}__toggle`}
        >
          <ToggleButton value="edit" sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem' }}>
            {t('users.permissions.canEdit')}
          </ToggleButton>
          <ToggleButton value="view" sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem' }}>
            {t('users.permissions.viewOnly')}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>
  );

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="users__permissions-tab" sx={{ py: 1 }}>
      {/* Messages */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Unit Permissions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {t('users.permissions.unitPermissions')}
        </Typography>

        {activeUnits.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('units.noUnits')}
          </Typography>
        ) : (
          <FormGroup>
            {activeUnits.map((unit) => (
              <PermissionRow
                key={unit.id}
                checked={isUnitChecked(unit.id)}
                label={unit.name}
                canEdit={getUnitCanEdit(unit.id)}
                onToggle={() => toggleUnit(unit.id)}
                onCanEditChange={(canEdit) => setUnitCanEdit(unit.id, canEdit)}
                testIdPrefix={`users__perm-unit--${unit.id}`}
              />
            ))}
          </FormGroup>
        )}
      </Paper>

      {/* Category Permissions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {t('users.permissions.categoryPermissions')}
        </Typography>

        {activeCategories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('common.noData')}
          </Typography>
        ) : (
          <FormGroup>
            {activeCategories.map((cat) => (
              <PermissionRow
                key={cat.id}
                checked={isCategoryChecked(cat.id)}
                label={cat.name + (cat.parent_id ? '' : ' ●')}
                canEdit={getCategoryCanEdit(cat.id)}
                onToggle={() => toggleCategory(cat.id)}
                onCanEditChange={(canEdit) => setCategoryCanEdit(cat.id, canEdit)}
                testIdPrefix={`users__perm-cat--${cat.id}`}
              />
            ))}
          </FormGroup>
        )}
      </Paper>

      {/* Actions */}
      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          data-testid="users__permissions__button--reset"
          variant="outlined"
          color="error"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          disabled={saving || (unitPerms.length === 0 && categoryPerms.length === 0)}
        >
          {t('users.permissions.resetPermissions')}
        </Button>

        <Button
          data-testid="users__permissions__button--save"
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </Box>
    </Box>
  );
}
